// Voice recording hook owning the Azure Speech SDK lifecycle.
//
// Acquires an ephemeral token from the backend, wires Azure Speech's
// `SpeechRecognizer` to the browser microphone, and surfaces partial +
// final transcripts plus an auto-submit signal triggered after the user
// pauses for `VOICE_INPUT_PAUSE_THRESHOLD_MS`.
//
// The Speech SDK handles the WebSocket protocol, audio framing, partial
// results, and auto-language-detection. We only own state machine,
// pause-detection timer, and error mapping.

import { useCallback, useEffect, useRef, useState } from "react";
import {
  AudioConfig,
  AutoDetectSourceLanguageConfig,
  CancellationReason,
  ResultReason,
  SpeechConfig,
  SpeechRecognizer,
} from "microsoft-cognitiveservices-speech-sdk";

import { useAuthFetch } from "../../../../utils/useAuthFetch";
import {
  VOICE_INPUT_NO_SPEECH_TIMEOUT_MS,
  VOICE_INPUT_PAUSE_THRESHOLD_MS,
  isVoiceInputSupported,
  mapMediaError,
  speechServiceError,
  tokenMintError,
  unsupportedBrowserError,
  VoiceInputError,
} from "./voiceInputUtils";

const AUTO_DETECT_LANGUAGES = ["en-US", "sv-SE", "de-DE"];

export type VoiceRecordingState =
  | "idle"
  | "requesting"
  | "recording"
  | "processing"
  | "error";

interface UseVoiceRecordingArgs {
  workspaceId: string;
  onSubmit: (transcript: string) => void;
  onPartial?: (transcript: string) => void;
}

interface UseVoiceRecordingReturn {
  state: VoiceRecordingState;
  error: VoiceInputError | null;
  start: () => Promise<void>;
  stop: () => void;
  cancel: () => void;
  partialTranscript: string;
}

interface VoiceTokenResponse {
  token: string;
  region: string;
  expires_at: string;
}

export function useVoiceRecording({
  workspaceId,
  onSubmit,
  onPartial,
}: UseVoiceRecordingArgs): UseVoiceRecordingReturn {
  const authFetch = useAuthFetch();
  const [state, setState] = useState<VoiceRecordingState>("idle");
  const [error, setError] = useState<VoiceInputError | null>(null);
  const [partialTranscript, setPartialTranscript] = useState("");

  const recognizerRef = useRef<SpeechRecognizer | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const pauseTimerRef = useRef<number | null>(null);
  const noSpeechTimerRef = useRef<number | null>(null);
  const finalBufferRef = useRef<string>("");
  // Mirror of the composed string currently shown in the composer (final buffer
  // plus any in-flight partial). stop() submits this so an explicit stop keeps
  // exactly what the user sees, including a partial that hasn't finalized yet.
  const displayedRef = useRef<string>("");
  const cancelledRef = useRef<boolean>(false);

  // Update the internal transcript state without touching the composer. Callers
  // that must also mutate the composer call onPartial explicitly.
  const setDisplayedTranscript = useCallback((value: string) => {
    displayedRef.current = value;
    setPartialTranscript(value);
  }, []);

  const clearPauseTimer = useCallback(() => {
    if (pauseTimerRef.current !== null) {
      window.clearTimeout(pauseTimerRef.current);
      pauseTimerRef.current = null;
    }
  }, []);

  const clearNoSpeechTimer = useCallback(() => {
    if (noSpeechTimerRef.current !== null) {
      window.clearTimeout(noSpeechTimerRef.current);
      noSpeechTimerRef.current = null;
    }
  }, []);

  const teardown = useCallback(() => {
    clearPauseTimer();
    clearNoSpeechTimer();
    const recognizer = recognizerRef.current;
    if (recognizer) {
      recognizerRef.current = null;
      try {
        recognizer.stopContinuousRecognitionAsync(
          () => recognizer.close(),
          () => recognizer.close(),
        );
      } catch {
        // SDK occasionally throws on already-closed recognizers; safe to ignore.
      }
    }
    const stream = mediaStreamRef.current;
    if (stream) {
      mediaStreamRef.current = null;
      stream.getTracks().forEach((track) => track.stop());
    }
  }, [clearNoSpeechTimer, clearPauseTimer]);

  const cancel = useCallback(() => {
    cancelledRef.current = true;
    // Discard means discard: if voice wrote a transcript into the composer,
    // clear it. If the user cancels before speaking, leave any pre-typed text
    // untouched (voice never overwrote it).
    const hadTranscript =
      displayedRef.current.trim().length > 0 ||
      finalBufferRef.current.trim().length > 0;
    finalBufferRef.current = "";
    setDisplayedTranscript("");
    if (hadTranscript) {
      onPartial?.("");
    }
    teardown();
    setState("idle");
    setError(null);
  }, [onPartial, setDisplayedTranscript, teardown]);

  const finalize = useCallback(() => {
    const transcript = finalBufferRef.current.trim();
    cancelledRef.current = true;
    teardown();
    if (transcript.length > 0) {
      setState("processing");
      onSubmit(transcript);
    }
    finalBufferRef.current = "";
    setDisplayedTranscript("");
    setState("idle");
  }, [onSubmit, setDisplayedTranscript, teardown]);

  // Explicit stop (mic-click / toggle while recording): finalize and KEEP the
  // transcript. Unlike the pause-timer finalize (which submits only finalized
  // results), stop submits the composed string currently shown so an in-flight
  // partial captured mid-utterance is not lost.
  const stop = useCallback(() => {
    const transcript = (
      displayedRef.current || finalBufferRef.current
    ).trim();
    cancelledRef.current = true;
    teardown();
    if (transcript.length > 0) {
      setState("processing");
      onSubmit(transcript);
    }
    finalBufferRef.current = "";
    setDisplayedTranscript("");
    setState("idle");
  }, [onSubmit, setDisplayedTranscript, teardown]);

  const schedulePauseSubmit = useCallback(() => {
    clearPauseTimer();
    pauseTimerRef.current = window.setTimeout(() => {
      if (cancelledRef.current) return;
      finalize();
    }, VOICE_INPUT_PAUSE_THRESHOLD_MS);
  }, [clearPauseTimer, finalize]);

  const start = useCallback(async () => {
    if (!isVoiceInputSupported()) {
      setError(unsupportedBrowserError());
      setState("error");
      return;
    }
    cancelledRef.current = false;
    finalBufferRef.current = "";
    setDisplayedTranscript("");
    setError(null);
    setState("requesting");

    let tokenResponse: VoiceTokenResponse;
    try {
      tokenResponse = (await authFetch(
        "/api/workspace/ida/voice-input/token",
        "voice-input-token",
        { headers: { "X-Workspace-Id": workspaceId } },
      )) as VoiceTokenResponse;
    } catch {
      setError(tokenMintError());
      setState("error");
      return;
    }

    let mediaStream: MediaStream;
    try {
      mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: { sampleRate: 16000, channelCount: 1 },
      });
    } catch (err) {
      setError(mapMediaError(err));
      setState("error");
      return;
    }
    mediaStreamRef.current = mediaStream;

    let recognizer: SpeechRecognizer;
    try {
      const speechConfig = SpeechConfig.fromAuthorizationToken(
        tokenResponse.token,
        tokenResponse.region,
      );
      // Azure Speech does not log audio by default; logging is an explicit
      // opt-in we never enable. Design doc §6 audio-not-retained posture
      // is therefore the default Speech configuration.
      const audioConfig = AudioConfig.fromStreamInput(mediaStream);
      const autoDetect = AutoDetectSourceLanguageConfig.fromLanguages(
        AUTO_DETECT_LANGUAGES,
      );
      recognizer = SpeechRecognizer.FromConfig(
        speechConfig,
        autoDetect,
        audioConfig,
      );
    } catch {
      teardown();
      setError(speechServiceError());
      setState("error");
      return;
    }
    recognizerRef.current = recognizer;

    recognizer.recognizing = (_sender, event) => {
      if (cancelledRef.current) return;
      const partial = event.result?.text ?? "";
      const composed = (finalBufferRef.current + " " + partial).trim();
      setDisplayedTranscript(composed);
      onPartial?.(composed);
      // Any new speech resets both the pause-submit and no-speech timers.
      clearNoSpeechTimer();
      clearPauseTimer();
    };
    recognizer.recognized = (_sender, event) => {
      if (cancelledRef.current) return;
      if (event.result?.reason === ResultReason.RecognizedSpeech) {
        const final = event.result.text ?? "";
        if (final.trim().length > 0) {
          finalBufferRef.current = (
            finalBufferRef.current +
            " " +
            final
          ).trim();
          setDisplayedTranscript(finalBufferRef.current);
          onPartial?.(finalBufferRef.current);
          schedulePauseSubmit();
        }
      }
    };
    recognizer.canceled = (_sender, event) => {
      if (cancelledRef.current) return;
      if (event.reason === CancellationReason.Error) {
        setError(speechServiceError());
        setState("error");
        teardown();
      }
    };

    recognizer.startContinuousRecognitionAsync(
      () => {
        if (cancelledRef.current) return;
        setState("recording");
        noSpeechTimerRef.current = window.setTimeout(() => {
          if (cancelledRef.current) return;
          if (finalBufferRef.current.trim().length === 0) {
            // No speech was ever recognized. Silently return to idle (johan
            // ruling #125: no notice). Leave any pre-typed composer text alone
            // (voice never wrote to it).
            cancelledRef.current = true;
            teardown();
            setDisplayedTranscript("");
            setState("idle");
          }
        }, VOICE_INPUT_NO_SPEECH_TIMEOUT_MS);
      },
      () => {
        setError(speechServiceError());
        setState("error");
        teardown();
      },
    );
  }, [
    authFetch,
    clearNoSpeechTimer,
    clearPauseTimer,
    onPartial,
    schedulePauseSubmit,
    setDisplayedTranscript,
    teardown,
    workspaceId,
  ]);

  useEffect(() => {
    return () => {
      cancelledRef.current = true;
      teardown();
    };
  }, [teardown]);

  return { state, error, start, stop, cancel, partialTranscript };
}
